import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { Button, Input, Skeleton, SectionErrorBoundary } from '@shared/ui';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { captureError } from '@shared/lib/observability';
import MyDataScope from './MyDataScope';
import { NamedIcon } from '@shared/ui/icons';
import SessionsPanel from './SessionsPanel';

interface ProfileRow {
  id: string;
  display_name: string | null;
  phone: string | null;
  locale: string | null;
}

export default function Profile() {
  const { t } = useTranslation('customer');
  const { user, hasFeature } = useAuth();
  const navigate = useNavigate();
  const invalidate = useInvalidateSupabase();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useSupabaseQuery<ProfileRow | null>(
    ['profile', user?.id],
    async () => {
      if (!user) return { data: null, error: null };
      return supabase
        .from('profiles')
        .select('id, display_name, phone, locale')
        .eq('id', user.id)
        .maybeSingle();
    },
    { enabled: !!user },
  );

  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (data) {
      // Sign-up seeds display_name with the e-mail; don't present that as a name.
      setDisplayName(data.display_name && data.display_name !== user?.email ? data.display_name : '');
      setPhone(data.phone ?? '');
    }
  }, [data]);

  const save = async () => {
    if (!user) return;
    setError(null);
    setSaved(false);

    const { error: e } = await supabase
      .from('profiles')
      .upsert(
        { id: user.id, display_name: displayName, phone },
        { onConflict: 'id' },
      );
    if (e) {
      setError(e.message);
      captureError(e, { where: 'customer.profile.save', userId: user.id });
      return;
    }
    setSaved(true);
    invalidate(['profile']);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <SectionErrorBoundary name="profile">
      <div style={{ maxWidth: 560 }}>
        <h2 className="c-page-title">{t('profile.title')}</h2>

        {isLoading ? (
          <Skeleton height={280} />
        ) : (
          <div className="c-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input
              label={t('profile.displayName')}
              placeholder={t('profile.displayNamePh', { defaultValue: 'Компания или ваше имя' })}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Input
              label={t('profile.phone')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div>
              <label className="field-label">{t('profile.language')}</label>
              <LanguageSwitcher />
            </div>
            <Input label={t('profile.email')} value={user?.email ?? ''} disabled />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
              <Button onClick={save}>{t('profile.save')}</Button>
              {saved && (
                <span style={{ color: 'var(--success, #2ecc71)', fontSize: 'var(--fs-body)' }}>
                  <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><NamedIcon name="check" size={13} color="var(--success, #2ecc71)" /></span> {t('profile.saved')}
                </span>
              )}
              {error && (
                <span style={{ color: 'var(--danger)', fontSize: 'var(--fs-body)' }}>{error}</span>
              )}
            </div>
          </div>
        )}

        {/* Followed brands moved to «Мой бренд», next to their analytics. */}
        <button type="button" onClick={() => navigate(hasFeature('an_my') ? '/customer/my-analytics' : '/customer/connect')} className="c-panel"
          style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: 12, minHeight: 56, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit', textAlign: 'left' }}>
          <span>{t('profile.brandsMoved', { defaultValue: 'Ваши бренды — в разделе «Мой бренд»' })}</span>
          <span aria-hidden="true" style={{ color: 'var(--accent)', fontSize: 18 }}>›</span>
        </button>
        <MyDataScope />
        <SessionsPanel />
      </div>
    </SectionErrorBoundary>
  );
}
